import { useState, useEffect, useRef, useCallback } from "react";

/**
 * BottomSheet - volet coulissant inspiré de @gorhom/bottom-sheet
 *
 * Props:
 *   - snapPoints: array of heights in % (e.g. ['25%', '50%', '90%'])
 *   - initialIndex: starting snap point (default 0)
 *   - children: content
 */
export default function BottomSheet({
  snapPoints = ["25%", "50%", "90%"],
  initialIndex = 0,
  children,
  backgroundClassName = "bg-[#101010]",
}) {
  const [index, setIndex] = useState(initialIndex);
  const [dragOffset, setDragOffset] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const startYRef = useRef(0);
  const baseHeightRef = useRef(0);

  const parsePct = (v) => parseFloat(v.replace("%", ""));
  const currentHeight = parsePct(snapPoints[index]);

  const snapToClosest = useCallback(
    (finalHeightPct) => {
      let closestIdx = 0;
      let bestDiff = Infinity;
      snapPoints.forEach((sp, i) => {
        const diff = Math.abs(parsePct(sp) - finalHeightPct);
        if (diff < bestDiff) {
          bestDiff = diff;
          closestIdx = i;
        }
      });
      setIndex(closestIdx);
      setDragOffset(0);
    },
    [snapPoints]
  );

  const handleStart = (clientY) => {
    startYRef.current = clientY;
    baseHeightRef.current = currentHeight;
    setIsDragging(true);
  };

  const handleMove = (clientY) => {
    if (!isDragging) return;
    const dy = startYRef.current - clientY; // positif = glissé vers le haut
    const vh = window.innerHeight;
    const newPct = Math.max(
      parsePct(snapPoints[0]) - 5,
      Math.min(95, baseHeightRef.current + (dy / vh) * 100)
    );
    setDragOffset(newPct - currentHeight);
  };

  const handleEnd = () => {
    if (!isDragging) return;
    setIsDragging(false);
    const finalHeight = currentHeight + dragOffset;
    snapToClosest(finalHeight);
  };

  useEffect(() => {
    if (!isDragging) return;
    const onTouchMove = (e) => handleMove(e.touches[0].clientY);
    const onTouchEnd = () => handleEnd();
    const onMouseMove = (e) => handleMove(e.clientY);
    const onMouseUp = () => handleEnd();
    window.addEventListener("touchmove", onTouchMove, { passive: false });
    window.addEventListener("touchend", onTouchEnd);
    window.addEventListener("mousemove", onMouseMove);
    window.addEventListener("mouseup", onMouseUp);
    return () => {
      window.removeEventListener("touchmove", onTouchMove);
      window.removeEventListener("touchend", onTouchEnd);
      window.removeEventListener("mousemove", onMouseMove);
      window.removeEventListener("mouseup", onMouseUp);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDragging, dragOffset, currentHeight]);

  const displayHeight = currentHeight + dragOffset;

  return (
    <div
      className={`fixed inset-x-0 bottom-0 z-40 ${backgroundClassName} rounded-t-3xl shadow-2xl flex flex-col`}
      style={{
        height: `${displayHeight}vh`,
        transition: isDragging ? "none" : "height 0.3s cubic-bezier(0.32, 0.72, 0, 1)",
        touchAction: "none",
      }}
      data-testid="bottom-sheet"
    >
      <div
        className="flex items-center justify-center pt-3 pb-2 cursor-grab active:cursor-grabbing select-none"
        onTouchStart={(e) => handleStart(e.touches[0].clientY)}
        onMouseDown={(e) => handleStart(e.clientY)}
        data-testid="bottom-sheet-handle"
      >
        <div className="w-10 h-1.5 rounded-full bg-[#555]" />
      </div>
      <div className="flex-1 overflow-y-auto overscroll-contain">
        {children}
      </div>
    </div>
  );
}
